import { type ElementType, useEffect, useRef } from 'react';

import { useInView } from '@/hooks/use-in-view';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { animate, ANIME_MS, stagger } from '@/lib/anime';
import { EASE } from '@/lib/motion-tokens';
import { cn } from '@/lib/utils';

export interface TextStaggerProps {
  /** Plain text to animate. */
  text: string;
  as?: ElementType;
  className?: string;
  /** Split unit for the stagger. */
  by?: 'chars' | 'words';
  /** Per-unit delay (ms). */
  step?: number;
  /** Rise distance (px). */
  y?: number;
}

/**
 * Per-character / per-word entrance driven by anime.js — a lighter, springier
 * counterpart to the GSAP {@link SplitReveal}. Reach for it on eyebrows,
 * kickers and short expressive lines. Splits in React (stable across anime
 * versions) and animates the spans on first view.
 *
 * Reveal is driven by the rect-based {@link useInView} hook rather than an
 * IntersectionObserver: IO never fires inside a `preserve-3d` ancestor and can
 * miss a high threshold on short lines, which stranded the text `opacity:0`.
 * Plain, fully visible text under reduced motion.
 */
export function TextStagger({
  text,
  as: Tag = 'span',
  className,
  by = 'chars',
  step = 22,
  y = 18,
}: TextStaggerProps) {
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const inView = useInView(ref, { once: true, disabled: reduced });
  const playedRef = useRef(false);

  const units =
    by === 'chars' ? Array.from(text) : text.split(/(\s+)/).filter((chunk) => chunk.length > 0);

  useEffect(() => {
    const el = ref.current;
    if (!el || reduced || !inView || playedRef.current) return;

    const targets = el.querySelectorAll<HTMLElement>('[data-unit]');
    if (targets.length === 0) return;
    playedRef.current = true;

    animate(Array.from(targets), {
      opacity: [0, 1],
      y: [y, 0],
      duration: ANIME_MS.slow,
      delay: stagger(step),
      ease: `cubicBezier(${EASE.out.join(', ')})`,
    });
  }, [reduced, inView, step, y, text, by]);

  return (
    <Tag ref={ref} className={cn('inline-block', className)}>
      {units.map((unit, index) =>
        /^\s+$/.test(unit) ? (
          // eslint-disable-next-line react/no-array-index-key -- split units are positional
          <span key={index}> </span>
        ) : (
          <span
            // eslint-disable-next-line react/no-array-index-key -- split units are positional
            key={index}
            data-unit
            className="inline-block whitespace-pre will-change-transform"
            style={reduced ? undefined : { opacity: 0 }}
          >
            {unit}
          </span>
        ),
      )}
    </Tag>
  );
}
